import React, { useEffect, useState } from "react";
import { Drawer, Spin, Button, Tag, Divider, Tooltip, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faServer,
  faGears,
  faEthernet,
  faBuildingShield,
  faElevator,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { isMobile } from "react-device-detect";
// CONTEXT
import { useWizard, DetailEntityType } from "./WizardContext";
// REDUX
import { useLazyGetAdapterQuery } from "../../redux/adapters/adapterAPI";
import { useLazyGetServerQuery } from "../../redux/servers/serverAPI";
import { useLazyGetBankQuery } from "../../redux/banks/bankAPI";
import { useLazyGetChannelQuery } from "../../redux/channels/channelAPI";
import { useLazyGetOrganizationAndChildsQuery } from "../../redux/organizations/organizationAPI";
// AUTH
import GETJwtToken from "../../redux/authentication/authentication";
// STORE
import { RootState } from "../../app/store";

const ENTITY_ICONS: Record<DetailEntityType, any> = {
  organization: faBuilding,
  server: faServer,
  adapter: faGears,
  channel: faEthernet,
  bank: faBuildingShield,
  lift: faElevator,
};

const ENTITY_LABELS: Record<DetailEntityType, string> = {
  organization: "Organization",
  server: "Server",
  adapter: "Adapter",
  channel: "Channel",
  bank: "Bank",
  lift: "Lift",
};

function CopyableRow({
  label,
  value,
}: {
  label: string;
  value: string | number | boolean | null | undefined;
}) {
  const displayValue = value != null ? String(value) : "--";

  const handleCopy = () => {
    if (value != null) {
      navigator.clipboard.writeText(String(value));
      message.success(`Copied ${label}`);
    }
  };

  return (
    <div className="generalStyles__flex drawer__container">
      <div>
        <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>{label}</p>
      </div>
      <div
        className="drawer__textContainer"
        style={{ display: "flex", alignItems: "center", gap: 4 }}
      >
        <p className="drawer__text" style={{ margin: 0, flex: 1 }}>
          {displayValue}
        </p>
        {value != null && (
          <Tooltip title="Copy">
            <Button
              type="text"
              size="small"
              icon={<FontAwesomeIcon icon={faCopy} style={{ fontSize: 11 }} />}
              onClick={handleCopy}
              style={{ opacity: 0.5, minWidth: 24 }}
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
}

export default function WizardDetailDrawer() {
  const { theme } = useSelector((state: RootState) => state.home);
  const { detailDrawer, closeDetailDrawer } = useWizard();
  const { open, entityType, entityId, data: passedData } = detailDrawer;

  const [loading, setLoading] = useState(false);
  const [entityData, setEntityData] = useState<Record<string, any> | null>(null);

  // API hooks for fetching entity details
  const [triggerGetServer] = useLazyGetServerQuery();
  const [triggerGetAdapter] = useLazyGetAdapterQuery();
  const [triggerGetBank] = useLazyGetBankQuery();
  const [triggerGetChannel] = useLazyGetChannelQuery();
  const [triggerGetOrganization] = useLazyGetOrganizationAndChildsQuery();

  useEffect(() => {
    if (!open || !entityType || !entityId) {
      setEntityData(null);
      return;
    }

    // If data was passed directly, use it
    if (passedData) {
      setEntityData(passedData);
      return;
    }

    // Otherwise fetch it
    const fetchData = async () => {
      setLoading(true);
      const token = await GETJwtToken();
      try {
        let result: any;
        switch (entityType) {
          case "server":
            result = await triggerGetServer({
              idServer: entityId,
              token,
            }).unwrap();
            break;
          case "adapter":
            result = await triggerGetAdapter({ id: entityId, token }).unwrap();
            break;
          case "bank":
            result = await triggerGetBank({ bankId: entityId, token }).unwrap();
            break;
          case "channel":
            result = await triggerGetChannel({
              channelId: entityId,
              token,
            }).unwrap();
            break;
          case "organization":
            result = await triggerGetOrganization({
              orgId: entityId,
              token,
            }).unwrap();
            break;
          default:
            break;
        }
        setEntityData(result?.data || result || null);
      } catch {
        setEntityData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [open, entityType, entityId, passedData]);

  if (!entityType) return null;

  const icon = ENTITY_ICONS[entityType];
  const label = ENTITY_LABELS[entityType];

  const renderFields = () => {
    if (!entityData) return <p style={{ color: "#888" }}>No data available</p>;

    switch (entityType) {
      case "organization":
        return (
          <>
            <CopyableRow label="ID" value={entityData.id} />
            <CopyableRow label="Name" value={entityData.name} />
            <CopyableRow label="Type" value={entityData.type} />
            <CopyableRow label="Address" value={entityData.address} />
            <CopyableRow label="City" value={entityData.city} />
            <CopyableRow label="State" value={entityData.state} />
            <CopyableRow label="Country" value={entityData.country} />
            <CopyableRow label="Phone" value={entityData.phone} />
            <CopyableRow label="Status" value={entityData.status} />
            <CopyableRow label="Parent ID" value={entityData.parent_id} />
          </>
        );
      case "server":
        return (
          <>
            <CopyableRow label="ID" value={entityData.id} />
            <CopyableRow label="Name" value={entityData.name} />
            <CopyableRow label="IP" value={entityData.ip} />
            <CopyableRow label="Active" value={entityData.active} />
            <CopyableRow label="Org ID" value={entityData.org_id} />
          </>
        );
      case "adapter":
        return (
          <>
            <CopyableRow label="ID" value={entityData.id} />
            <CopyableRow label="Name" value={entityData.name} />
            <CopyableRow label="Type" value={entityData.type} />
            <CopyableRow label="Protocol" value={entityData.protocol_type} />
            <CopyableRow label="Lift Type" value={entityData.lift_type} />
            <CopyableRow label="Version" value={entityData.version} />
            <CopyableRow label="Server ID" value={entityData.server_id} />
          </>
        );
      case "channel":
        return (
          <>
            <CopyableRow label="ID" value={entityData.id} />
            <CopyableRow label="Name" value={entityData.name} />
            <CopyableRow label="Type" value={entityData.type} />
            <CopyableRow label="Server ID" value={entityData.server_id} />
          </>
        );
      case "bank":
        return (
          <>
            <CopyableRow label="ID" value={entityData.id} />
            <CopyableRow label="Name" value={entityData.name} />
            <CopyableRow label="Type" value={entityData.type} />
            <CopyableRow label="Lifts" value={entityData.number_of_lifts} />
            <CopyableRow label="Floors" value={entityData.floor_count} />
            <CopyableRow label="Stops" value={entityData.stops} />
            <CopyableRow label="Below Lobby" value={entityData.floor_below_lobby} />
            <CopyableRow label="Rear Doors" value={entityData.rear_doors} />
            <CopyableRow label="Channel ID" value={entityData.channel_id} />
          </>
        );
      case "lift":
        return (
          <>
            <CopyableRow label="ID" value={entityData.id} />
            <CopyableRow label="Name" value={entityData.name} />
            <CopyableRow label="Position" value={entityData.position} />
            <CopyableRow label="Type" value={entityData.type} />
            <CopyableRow label="Bank ID" value={entityData.bank_id} />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Drawer
      width={isMobile ? "100%" : 360}
      placement="right"
      onClose={closeDetailDrawer}
      open={open}
      title={
        <span>
          <FontAwesomeIcon icon={icon} style={{ marginRight: 8 }} />
          {label} Details
        </span>
      }
    >
      <Spin spinning={loading}>
        <div className={`drawer__box__${theme}`} style={{ padding: 12 }}>
          {entityData?.name && (
            <>
              <h4 style={{ margin: "0 0 4px 0" }}>{entityData.name}</h4>
              {entityData?.type && (
                <Tag color="blue" style={{ marginBottom: 8 }}>
                  {entityData.type}
                </Tag>
              )}
              <Divider style={{ margin: "8px 0" }} />
            </>
          )}
          {renderFields()}
        </div>

        <div style={{ marginTop: 24, textAlign: "right" }}>
          <Button onClick={closeDetailDrawer}>Close</Button>
        </div>
      </Spin>
    </Drawer>
  );
}
